<template>
  <div class="vx-row pa-5">

    <div class="vx-col w-full ml-5">
      <div class="vx-row">
        <div class="vx-col w-full">
          <p class="mb-4"><strong>Follow the steps below to set up two-factor authentication:</strong></p>
          <vs-list-header class="mt-10" title="Step 1"></vs-list-header>
          <p>Download a two-factor Authenticator app (like <em><strong>1Password</strong></em>, <em><strong>Google Authenticator</strong></em>, <em><strong>Microsoft Authenticator</strong></em>, etc.)</p>
        </div>
      </div>
      <div class="vx-row">
        <div class="vx-col w-full">
          <vs-list-header class="mt-10" title="Step 2"></vs-list-header>
          <table>
            <tr>
              <td>
                <qrcode-vue v-if="qrCode" :value="qrCode" :size="150" render-as="svg" class="mr-10"></qrcode-vue>
              </td>
              <td>
                <p class="my-5">Scan this <strong>QR Code</strong> with your Authenticator app</p>
                <p><strong>Can't scan the code?</strong> You can copy and paste the code to your application manually</p>
                <vs-input
                  class="w-full my-5"
                  icon="icon icon-copy"
                  icon-after
                  icon-pack="feather"
                  :value="mfaCode"
                  @icon-click="copyMFACode"
                >
              </vs-input>
              <vs-button type="flat" color="warning" icon="refresh" icon-after @click="onRefreshTOTP">Refresh</vs-button>
              </td>
            </tr>
          </table>
        </div>
      </div>
      <div class="vx-row">
        <div class="vx-col w-full">
          <vs-list-header class="my-10" title="Step 3"></vs-list-header>
          <table>
            <tr>
              <td>
                <p class="mr-10">Enter the resulting verification code:</p>
              </td>
              <td>
                <form autocomplete="on">
                  <vs-input
                    v-show="false"
                    v-model="cognitoUser.username"
                    type="text"
                    id="username"
                    name="username"
                    autocomplete="username">
                  </vs-input>

                  <sm-input-verification-code
                    id="one-time-code"
                    name="one-time-code"
                    autocomplete="one-time-code"
                    v-model="verificationCode"
                    :length="6"
                    :font-size="35"
                    :input-color="verificationError ? 'rgb(var(--vs-danger))' : 'rgb(var(--vs-primary))'"
                    :font-color="verificationError ? 'rgb(var(--vs-danger))' : 'rgb(var(--vs-primary))'"
                    :allow-paste="true"
                    :only-number="true"
                    v-validate="'required'"
                    data-vv-as="Verification Code"
                    :danger="errors.has('one-time-code')"
                    :danger-text="errors.first('one-time-code')">
                  </sm-input-verification-code>
                </form>
              </td>
            </tr>
          </table>
        </div>
        <div class="vx-col w-full">
          <vs-alert color="danger" :active="verificationError" class="mt-5 mb-5 text-center">
            Invalid code
          </vs-alert>
        </div>
      </div>
      <div class="vx-row justify-center mt-10">
        <div class="vx-col justify-center">
          <vs-button class="mr-1" @click="onSubmit" :disabled="errors.any() || !isFormDirty">Enable two-factor authentication</vs-button>
          <vs-button class="ml-1" type="flat" @click="onCancel">Cancel</vs-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {Auth} from 'aws-amplify';
import QrcodeVue from 'qrcode.vue';
import SmInputVerificationCode from '@/components/starling/sm-input-verification-code/sm-input-verification-code';

/* eslint-disable security/detect-object-injection */
export default {
  name: 'security-mfa-setup',
  components: {QrcodeVue, SmInputVerificationCode},
  props: {
    cognitoUser: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      mfaCode: null,
      qrCode: null,
      verificationCode: null,
      verificationError: false
    };
  },
  computed: {
    isFormDirty () {
      return Object.keys(this.fields).some(key => this.fields[key] && this.fields[key].dirty);
    },
    totpIssuer () {
      return `Starling%20Minds${process.env.VUE_APP_ENV_NAME ? `%20-%20${process.env.VUE_APP_ENV_NAME}` : ''}`;
    }
  },
  mounted () {
    this.onRefreshTOTP();
  },
  methods: {
    onRefreshTOTP () {
      Auth.setupTOTP(this.cognitoUser).then((code) => {
        this.mfaCode = code;
        this.qrCode = `otpauth://totp/${this.totpIssuer}:${this.cognitoUser.username}?secret=${code}&issuer=${this.totpIssuer}`;
      }).catch(err => {
        this.$log.error(err);
        this.$emit('error');
      });
    },
    onCancel () {
      this.$emit('updated', false);
    },
    onSubmit () {
      if (this.verificationCode.length < 6) {
        this.verificationError = true;
        return;
      }

      this.verificationError = false;
      Auth.verifyTotpToken(this.cognitoUser, this.verificationCode).then(() => {
        return Auth.setPreferredMFA(this.cognitoUser, 'TOTP').then(() => {
          this.$vs.notify({
            title: 'Two-factor authentication configured',
            text: 'A verification code will be asked when you sign-in.',
            iconPack: 'feather',
            icon: 'icon-check-circle',
            color: 'success'
          });
          this.$emit('updated', true);
        });

      }).catch((err) => {
        // Token is not verified
        this.$log.error(err);
        this.verificationError = true;
      });
    },
    copyMFACode () {
      navigator.clipboard.writeText(this.mfaCode);
    }
  }
};
</script>
